import React from 'react';
import { SetupHelpPanel } from './SetupHelpPanel';

interface UsernameSetupHelpProps {
  /**
   * Username entered by user (for dynamic SQL)
   */
  username: string;
  /**
   * Role name entered by user (for service user setup)
   */
  roleName: string;
  /**
   * Warehouse name entered by user (for service user setup)
   */
  warehouseName: string;
}

/**
 * UsernameSetupHelp Component
 *
 * Displays setup instructions for creating a dedicated Snowflake service user
 * with dynamic SQL that updates based on user input.
 *
 * @component
 * @example
 * ```tsx
 * <UsernameSetupHelp
 *   username="DATAOPS_ADMIN_USER"
 *   roleName="DATAOPS_ADMIN_ROLE"
 *   warehouseName="DATAOPS_ADMIN_WAREHOUSE"
 * />
 * ```
 */
export const UsernameSetupHelp: React.FC<UsernameSetupHelpProps> = ({ username, roleName, warehouseName }) => {
  // Use defaults if values are empty
  const displayUsername = username.trim() !== '' ? username.trim() : 'DATAOPS_ADMIN_USER';
  const displayRoleName = roleName.trim() !== '' ? roleName.trim() : 'DATAOPS_ADMIN_ROLE';
  const displayWarehouseName = warehouseName.trim() !== '' ? warehouseName.trim() : 'DATAOPS_ADMIN_WAREHOUSE';

  const serviceUserSQL = `-- Create a dedicated service user (recommended)
USE ROLE USERADMIN;
CREATE USER IF NOT EXISTS ${displayUsername}
    TYPE = SERVICE
    DEFAULT_WAREHOUSE = ${displayWarehouseName}
    DEFAULT_ROLE = ${displayRoleName}
    DISPLAY_NAME = 'DataOps Admin User';

USE ROLE SECURITYADMIN;
GRANT ROLE ${displayRoleName} TO USER ${displayUsername};

-- If you need to create resource monitors, also grant ACCOUNTADMIN temporarily:
-- USE ROLE SECURITYADMIN;
-- GRANT ROLE ACCOUNTADMIN TO USER ${displayUsername};  -- remove after setup if not needed`;

  return (
    <SetupHelpPanel
      id="username-setup-help"
      title="Setup help"
      purpose="Create a dedicated service user for DataOps with appropriate defaults and role assignment."
      sqlBlocks={[
        {
          code: serviceUserSQL,
          runAsRole: 'USERADMIN and SECURITYADMIN',
        },
      ]}
      nextSteps={[
        'Paste and run in a worksheet (switch roles as shown).',
        `Enter "${displayUsername}" in the Username field here.`,
        'This service user will use the role and warehouse you configure in the other fields.',
      ]}
      notes={[
        'Service users (TYPE = SERVICE) are recommended for automated systems like DataOps.',
        'If using an existing user instead, you can skip this step.',
      ]}
      storageKey="username-help-expanded"
    />
  );
};
